/**
 * Dashboard Configuration Dispatcher is responsible for manipulating unSavedDashboardConfig and
 * dashboardConfig. These manipulations include operations such as fetch, save, add widgets, delete widget and update
 * dashboard layout.
 *
 * Difference between unSavedDashboardConfig and dashboardConfig and priorityDashboardConfig:
 * 1. unSavedDashboardConfig:  Used to manage the current state of the dashboard shown to the user. Whenever a user
 *    updates their dashboard, this is the state object that is updated
 * 1. dashboardConfig: Used to store the dashboard that is fetched from WCSS. This is updated when the user saves their
 *    dashboard changes.
 * 1. priorityDashboardConfig: When enabled, used to display the separate dashboard above the main dashboard. This will
 *    show priority widgets, and alerting widgets
 */

import {
  getDashboardConfiguration,
  getWidgets,
  putDashboardConfiguration,
} from "../../network/WCSSNetwork";
import UIUtils from "../../../util/UIUtils";
import { getRegion } from "../../../util/Util";
import { getUserLoginFromParent } from "../../../handler/ParentDataHandler";
import { CONFIG_GROUP_TYPES } from "../../../util/Constants";

export const DASHBOARD_CONFIG_FETCH_START = "DASHBOARD_CONFIG_FETCH_START";
export const DASHBOARD_CONFIG_FETCH_SUCCESS = "DASHBOARD_CONFIG_FETCH_SUCCESS";
export const DASHBOARD_CONFIG_FETCH_ERROR = "DASHBOARD_CONFIG_FETCH_ERROR";

export const DASHBOARD_CONFIG_SAVE_SUCCESS = "DASHBOARD_CONFIG_SAVE_SUCCESS";
export const DASHBOARD_CONFIG_SAVE_ERROR = "DASHBOARD_CONFIG_SAVE_ERROR";

export const UNSAVED_DASHBOARD_CONFIG_STATE_CHANGE =
  "UNSAVED_DASHBOARD_CONFIG_STATE_CHANGE";

export const PRIORITY_DASHBOARD_CONFIG_FETCH_START =
  "PRIORITY_DASHBOARD_CONFIG_FETCH_START";
export const PRIORITY_DASHBOARD_CONFIG_FETCH_SUCCESS =
  "PRIORITY_DASHBOARD_CONFIG_FETCH_SUCCESS";
export const PRIORITY_DASHBOARD_CONFIG_FETCH_ERROR =
  "PRIORITY_DASHBOARD_CONFIG_FETCH_ERROR";

export const WIDGET_SCROLL_COMPLETED = "WIDGET_SCROLL_COMPLETED";

export const UPDATE_METRIC_SELECTED_STAT = "UPDATE_METRIC_SELECTED_STAT";

async function getUserLogin() {
  const userLogin = await getUserLoginFromParent();
  return userLogin.name.replaceAll('"', "");
}

/**
 * Dispatch function to fetch dashboard configuration from WCSS
 * @param businessUnit
 * @param configurationGroupType
 * @param page
 * @param viewTypes
 * @returns {(function(*): Promise<void>)|*}
 */
export function fetchDashboardConfiguration(
  businessUnit,
  configurationGroupType,
  page,
  viewTypes,
) {
  return async (dispatch) => {
    dispatch({
      type:
        configurationGroupType === CONFIG_GROUP_TYPES.PRIORITY
          ? PRIORITY_DASHBOARD_CONFIG_FETCH_START
          : DASHBOARD_CONFIG_FETCH_START,
    });

    try {
      const userLogin = await getUserLogin();
      // Get Dashboard config from WCSS
      const getDashboardConfigResponse = await getDashboardConfiguration(
        businessUnit,
        configurationGroupType,
        getRegion(),
        UIUtils.getDeviceType(),
        page,
        viewTypes,
        userLogin,
      );
      // Extract widget IDs from the config and deduplicate
      const widgetIds = [
        ...new Set(
          getDashboardConfigResponse.data.config.widgets.map(
            (widget) => widget.widgetId,
          ),
        ),
      ];

      // Get all widget definitions from WCSS
      let getWidgetsResponse = await getWidgets(widgetIds);

      // Combine responses together
      getDashboardConfigResponse.data.config.widgets =
        getDashboardConfigResponse.data.config.widgets.map((widget) => {
          const widgetDefinition = getWidgetsResponse.data.widgets.find(
            (def) => def.widgetId === widget.widgetId,
          );
          return {
            ...widget,
            widgetDefinition: JSON.parse(widgetDefinition.config),
          };
        });

      dispatch({
        type:
          configurationGroupType === CONFIG_GROUP_TYPES.PRIORITY
            ? PRIORITY_DASHBOARD_CONFIG_FETCH_SUCCESS
            : DASHBOARD_CONFIG_FETCH_SUCCESS,
        payload: getDashboardConfigResponse.data.config,
      });
    } catch (err) {
      dispatch({
        type:
          configurationGroupType === CONFIG_GROUP_TYPES.PRIORITY
            ? PRIORITY_DASHBOARD_CONFIG_FETCH_ERROR
            : DASHBOARD_CONFIG_FETCH_ERROR,
        error: err.message,
      });
    }
  };
}

/**
 * Dispatch function to save dashboard config to WCSS
 * @param businessUnit
 * @param configurationGroupType
 * @param page
 * @param viewType
 * @returns {(function(*, *): Promise<void>)|*}
 */
export function saveDashboardConfiguration(
  businessUnit,
  configurationGroupType,
  page,
  viewType,
) {
  return async (dispatch, getState) => {
    try {
      // Copying as we are deleting widgetDefinition below
      // This is not technically needed, as WCSS strips this out automatically,
      // however doing this reduces bytes sent over network
      const copyUnsavedDashboard = JSON.parse(
        JSON.stringify(getState().unSavedDashboardConfig),
      );
      copyUnsavedDashboard.widgets.forEach((w) => {
        delete w.widgetDefinition;
      });

      const userLogin = await getUserLogin();
      await putDashboardConfiguration(
        businessUnit,
        configurationGroupType,
        getRegion(),
        UIUtils.getDeviceType(),
        page,
        viewType,
        copyUnsavedDashboard,
        userLogin,
      );

      dispatch({
        type: DASHBOARD_CONFIG_SAVE_SUCCESS,
        payload: getState().unSavedDashboardConfig,
      });
    } catch (err) {
      dispatch({
        type: DASHBOARD_CONFIG_SAVE_ERROR,
        error: err.message,
      });
    }
  };
}

/**
 * Function used to update the unsaved dashboard config when user edits the dashboard layout
 * @param dashboardConfig
 * @param layout
 * @param layouts
 * @returns {(function(*): Promise<void>)|*}
 */
export function updateDashboardConfigLayout(dashboardConfig, layout, layouts) {
  return async (dispatch) => {
    // Create updated config with new widget positions
    const updatedDashboardConfig = {
      ...dashboardConfig,
      widgets: dashboardConfig.widgets.map((widget) => {
        // Find the corresponding layout item for this widget
        const layoutItem = layout.find(
          (item) => item.i === widget.widgetId.toString(),
        );
        if (layoutItem) {
          return {
            ...widget,
            size: {
              x: layoutItem.x,
              y: layoutItem.y,
              w: layoutItem.w,
              h: layoutItem.h,
            },
          };
        } else {
          return widget;
        }
      }),
    };

    dispatch({
      type: UNSAVED_DASHBOARD_CONFIG_STATE_CHANGE,
      payload: updatedDashboardConfig,
    });
  };
}

/**
 * Function to add widgets to the unsaved dashboard config
 * @param widgetsToAdd
 * @returns {(function(*, *): Promise<void>)|*}
 */
export function addWidgetsToDashboardConfig(widgetsToAdd) {
  return async (dispatch, getState) => {
    const currentConfig = getState().unSavedDashboardConfig;

    if (!currentConfig || !widgetsToAdd || widgetsToAdd.length === 0) {
      return;
    }

    // Get the last added widget ID for scroll-to-focus functionality
    const lastAddedWidget = widgetsToAdd[widgetsToAdd.length - 1];
    const lastAddedWidgetId = lastAddedWidget ? lastAddedWidget.widgetId : null;

    const updatedConfig = {
      ...currentConfig,
      widgets: [...(currentConfig.widgets || []), ...widgetsToAdd],
      lastAddedWidgetId: lastAddedWidgetId,
    };

    dispatch({
      type: UNSAVED_DASHBOARD_CONFIG_STATE_CHANGE,
      payload: updatedConfig,
    });
  };
}

/**
 * Function to delete widgets from the unsaved dashboard config
 * @param widgetIdToDelete
 * @returns {(function(*, *): Promise<void>)|*}
 */
export function deleteWidgetsFromDashboardConfig(widgetIdToDelete) {
  return async (dispatch, getState) => {
    const currentConfig = getState().unSavedDashboardConfig;

    if (!currentConfig || !widgetIdToDelete) {
      return;
    }

    const newWidgetsList = currentConfig.widgets.filter(
      (widget) => widget.widgetId !== widgetIdToDelete,
    );

    const updatedConfig = {
      ...currentConfig,
      widgets: newWidgetsList,
    };

    dispatch({
      type: UNSAVED_DASHBOARD_CONFIG_STATE_CHANGE,
      payload: updatedConfig,
    });
  };
}

/**
 * Function to update the selected stat for a specific metric in a widget
 * @param widgetId - ID of the widget containing the metric
 * @param metricId - ID of the metric to update
 * @param selectedStat - The selected statistic value
 * @returns {(function(*, *): Promise<void>)|*}
 */
export function updateMetricSelectedStat(widgetId, metricId, selectedStat) {
  return async (dispatch, getState) => {
    const currentConfig = getState().unSavedDashboardConfig;

    if (!currentConfig || !widgetId || !metricId || !selectedStat) {
      return;
    }

    const updatedConfig = {
      ...currentConfig,
      widgets: currentConfig.widgets.map((widget) => {
        if (widget.widgetId === widgetId) {
          return {
            ...widget,
            widgetDefinition: {
              ...widget.widgetDefinition,
              metrics: widget.widgetDefinition.metrics.map((metric) => {
                if (metric.id === metricId) {
                  return {
                    ...metric,
                    selectedStat: selectedStat,
                  };
                }
                return metric;
              }),
            },
          };
        }
        return widget;
      }),
    };

    dispatch({
      type: UPDATE_METRIC_SELECTED_STAT,
      payload: updatedConfig,
    });
  };
}



// WEBPACK FOOTER //
// ./src/components/mySCC/common/thunks/DashboardConfigurationDispatcher.js